/**
 * Dashboard Chart Section Component
 * 
 * Renders a chart section from dashboard API response.
 * Uses Recharts for generic charts, custom components for specialized visualizations.
 * Generic and reusable - no doctype-specific logic.
 */

import { useMemo, useState } from "react";
import type { DashboardSection } from "@/lib/erp/docs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cn } from "@/lib/utils";
import { StockActionsModal } from "./StockActionsModal";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { designTokens } from "@/config/design-tokens";

interface DashboardChartSectionProps {
  section: DashboardSection;
  itemCode?: string; // Item code for stock actions (only for Item doctype)
}

/**
 * Minimal Stock Balance Chart Component
 * 
 * Renders a simple horizontal bar showing:
 * - Left: Reserved Qty (red)
 * - Right: Actual Qty (green) + Incoming Qty (blue)
 * - Warehouse list with aggregated stock per warehouse
 * 
 * Generic and reusable - works with any series that has reserved_qty, actual_qty, incoming_qty.
 */
function StockBalanceChart({ section, itemCode, onStockActionClick }: { 
  section: DashboardSection;
  itemCode?: string;
  onStockActionClick?: () => void;
}) {
  const { language } = useLanguage();
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      actual: t("actual", "Actual"),
      reserved: t("reserved", "Reserved"),
      incoming: t("incoming", "Incoming"),
      available: t("available", "Available"),
      locations: t("locations", "Locations"),
      totalStock: t("total_stock", "Total Stock"),
      percentOfTotal: t("percent_of_total", "% of Total"),
    };
  }, [language]);
  const { label, series = [] } = section;
  
  // Debug logging (dev only)
  if (import.meta.env.DEV) {
    console.log('[StockBalanceChart] Rendering with series:', {
      seriesIds: series.map(s => s.id),
      reserved: series.find(s => s.id === 'reserved_qty')?.data[0]?.y,
      actual: series.find(s => s.id === 'actual_qty')?.data[0]?.y,
      incoming: series.find(s => s.id === 'incoming_qty')?.data[0]?.y,
    });
  }
  
  // Extract values from series
  const actual = series.find(s => s.id === 'actual_qty')?.data[0]?.y ?? 0;
  const reserved = series.find(s => s.id === 'reserved_qty')?.data[0]?.y ?? 0;
  const incoming = series.find(s => s.id === 'incoming_qty')?.data[0]?.y ?? 0;
  
  // Reserved is a subset of Actual; do not double-count it in the bar/total.
  const reservedNormalized = Math.max(0, Math.min(reserved, actual));
  const available = Math.max(0, actual - reservedNormalized);
  const total = actual + incoming; // = available + reservedNormalized + incoming
  
  // Get metadata for warehouse count and total stock
  const warehouseCount = section.meta?.warehouse_count ?? 0;
  const totalStock = section.meta?.total_stock ?? total;
  
  // Calculate percentages for bar visualization (Available, Reserved, Incoming)
  const availablePct = total > 0 ? (available / total) * 100 : 0;
  const reservedPct = total > 0 ? (reservedNormalized / total) * 100 : 0;
  const incomingPct = total > 0 ? (incoming / total) * 100 : 0;
  
  // Percentage of total stock (if totalStock is different from calculated total)
  const percentageOfTotal = totalStock > 0 ? Math.round((total / totalStock) * 100) : 0;
  
  // Format numbers
  const formatNumber = (num: number) => {
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };
  
  return (
    <div 
      className={cn(
        "rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-6",
        itemCode && onStockActionClick && "cursor-pointer hover:shadow-md transition-shadow duration-200"
      )}
      onClick={itemCode && onStockActionClick ? onStockActionClick : undefined}
      role={itemCode && onStockActionClick ? "button" : undefined}
      tabIndex={itemCode && onStockActionClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (itemCode && onStockActionClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onStockActionClick();
        }
      }}
    >
      {label && (
        <div className="mb-4">
          <h3 className="text-[11px] font-semibold text-foreground uppercase tracking-wider">{label}</h3>
        </div>
      )}
      
      {/* Aggregated Metrics - Apple style: Actual, Reserved, Incoming, Available */}
      <div className="grid grid-cols-4 gap-4 mb-4 pb-4 border-b border-border/15">
        <div className="flex flex-col space-y-1">
          <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">{ui.actual}</span>
          <span className="text-2xl font-semibold text-foreground tabular-nums">{formatNumber(actual)}</span>
        </div>
        <div className="flex flex-col space-y-1">
          <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">{ui.reserved}</span>
          <span className="text-2xl font-semibold text-foreground tabular-nums">{formatNumber(reserved)}</span>
        </div>
        <div className="flex flex-col space-y-1">
          <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">{ui.incoming}</span>
          <span className="text-2xl font-semibold text-foreground tabular-nums">{formatNumber(incoming)}</span>
        </div>
        <div className="flex flex-col space-y-1">
          <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">{ui.available}</span>
          <span className={cn(
            "text-2xl font-semibold tabular-nums",
            available > 0 ? "text-emerald-600/90 dark:text-emerald-500/90" : "text-foreground"
          )}>
            {formatNumber(available)}
          </span>
        </div>
      </div>
      
      {/* Additional Info: Locations Count and Total Stock */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-border/15 text-[10px] text-muted-foreground/70">
        <div className="flex items-center gap-2">
          <span className="font-medium uppercase tracking-wider">{ui.locations}:</span>
          <span className="font-semibold text-foreground tabular-nums">{warehouseCount}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium uppercase tracking-wider">{ui.totalStock}:</span>
          <span className="font-semibold text-foreground tabular-nums">{formatNumber(total)}</span>
        </div>
        {percentageOfTotal > 0 && percentageOfTotal !== 100 && (
          <div className="flex items-center gap-2">
            <span className="font-medium uppercase tracking-wider">{ui.percentOfTotal}:</span>
            <span className="font-semibold text-foreground tabular-nums">{percentageOfTotal}%</span>
          </div>
        )}
      </div>
      
      {/* Horizontal bar - Apple style: Available, Reserved, Incoming */}
      <div className="mb-4">
        <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted/60 dark:bg-muted/40 flex">
          {/* Available (left) */}
          {availablePct > 0 && (
            <div
              style={{ width: `${availablePct}%` }}
              className="bg-emerald-500/90 dark:bg-emerald-600/90 transition-all duration-300"
            />
          )}
          {/* Reserved (middle) */}
          {reservedPct > 0 && (
            <div
              style={{ width: `${reservedPct}%` }}
              className="bg-amber-500/90 dark:bg-amber-600/90 transition-all duration-300"
            />
          )}
          {/* Incoming (right) */}
          {incomingPct > 0 && (
            <div
              style={{ width: `${incomingPct}%` }}
              className="bg-sky-500/90 dark:bg-sky-600/90 transition-all duration-300"
            />
          )}
        </div>
      </div>
      
      {/* Legend with totals - Order: Available, Reserved, Incoming */}
      <div className="flex items-center gap-3 text-[9px] font-normal text-muted-foreground/70 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-emerald-400/80 dark:bg-emerald-500/80" />
          <span className="font-medium">{ui.available}</span>
          <span className="tabular-nums">{formatNumber(available)}</span>
        </div>
        <div className="w-1 h-1 rounded-full bg-muted-foreground/30" />
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-amber-500/90 dark:bg-amber-600/90" />
          <span className="font-medium">{ui.reserved}</span>
          <span className="tabular-nums">{formatNumber(reservedNormalized)}</span>
        </div>
        <div className="w-1 h-1 rounded-full bg-muted-foreground/30" />
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-sky-500/90 dark:bg-sky-600/90" />
          <span className="font-medium">{ui.incoming}</span>
          <span className="tabular-nums">{formatNumber(incoming)}</span>
        </div>
      </div>
      
      {/* Note: Empty state for no warehouses is handled in stock levels section */}
    </div>
  );
}

export function DashboardChartSection({ section, itemCode }: DashboardChartSectionProps) {
  const { label, series = [], meta, error } = section;
  const chartType = meta?.chart_type || "bar";
  const { language } = useLanguage();
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      noDataAvailable: t("no_data_available", "No data available"),
      chartTypeNotSupportedTemplate: t(
        "chart_type_not_supported",
        'Chart type "{chartType}" is not yet supported. Only "bar" and "stock_balance" charts are available.'
      ),
    };
  }, [language]);
  
  // Add state for Stock Actions Modal (must be at top level - hooks rule)
  const [stockActionsModalOpen, setStockActionsModalOpen] = useState(false);
  
  // Debug logging (dev only)
  if (import.meta.env.DEV) {
    console.log('[DashboardChartSection]', {
      sectionId: section.id,
      chartType,
      meta,
      seriesCount: series.length,
      seriesIds: series.map(s => s.id),
    });
  }
  
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }
  
  // Special case: stock_balance chart (minimal horizontal bar)
  // Check both exact match and case-insensitive for safety
  // Handle stock_balance BEFORE empty series check so we can show helpful empty state
  const isStockBalance = chartType === "stock_balance" || chartType?.toLowerCase() === "stock_balance";
  
  if (isStockBalance) {
    if (import.meta.env.DEV) {
      console.log('[DashboardChartSection] ✅ Rendering StockBalanceChart for section:', section.id, {
        chartType,
        meta,
      });
    }
    
    return (
      <>
        <StockBalanceChart 
          section={section} 
          itemCode={itemCode}
          onStockActionClick={itemCode ? () => setStockActionsModalOpen(true) : undefined}
        />
        {itemCode && (
          <StockActionsModal
            open={stockActionsModalOpen}
            onOpenChange={setStockActionsModalOpen}
            itemCode={itemCode}
            lockWarehouses={false} // Allow full warehouse selection from stock summary
          />
        )}
      </>
    );
  }
  
  // For non-stock_balance charts, check if series is empty
  if (!series || series.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/20 p-6">
        <p className="text-sm text-muted-foreground text-center">{ui.noDataAvailable}</p>
      </div>
    );
  }
  
  // Debug: Log if we're NOT using stock_balance (especially for stock_summary)
  if (import.meta.env.DEV && section.id === "stock_summary") {
    console.warn('[DashboardChartSection] ⚠️ Stock Summary section NOT using stock_balance!', {
      chartType,
      expected: "stock_balance",
      meta,
      metaChartType: meta?.chart_type,
      sectionId: section.id,
    });
  }
  
  // Transform series data for Recharts
  // Group data by x value (category) to create stacked bars
  const categories = new Set<string>();
  series.forEach(s => {
    s.data.forEach(d => categories.add(d.x));
  });
  
  interface ChartDataPoint {
    category: string;
    [seriesId: string]: string | number;  // category is string, series values are numbers
  }
  
  const chartData: ChartDataPoint[] = Array.from(categories).map(category => {
    const dataPoint: ChartDataPoint = { category };
    series.forEach(s => {
      const point = s.data.find(d => d.x === category);
      dataPoint[s.id] = point?.y ?? 0;
    });
    return dataPoint;
  });
  
  // Color palette for series (Apple-style colors)
  const colors = [
    '#007AFF', // Blue
    '#34C759', // Green
    '#FF9500', // Orange
    '#FF3B30', // Red
    '#AF52DE', // Purple
    '#FF2D55', // Pink
  ];
  
  // For now, only support bar charts (generic) and stock_balance (special)
  if (chartType !== "bar") {
    const msg = ui.chartTypeNotSupportedTemplate.replace("{chartType}", String(chartType));
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/20 p-6">
        <p className="text-sm text-muted-foreground text-center">
          {msg}
        </p>
      </div>
    );
  }
  
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
      {label && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        </div>
      )}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="hsl(214.3 31.8% 91.4%)"
            className="dark:stroke-gray-800" 
          />
          <XAxis 
            dataKey="category" 
            tick={{ fontSize: 12, fill: 'rgb(107, 114, 128)' }}
            className="dark:[&_text]:fill-gray-400"
          />
          <YAxis 
            tick={{ fontSize: 12, fill: 'rgb(107, 114, 128)' }}
            className="dark:[&_text]:fill-gray-400"
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'white',
              border: designTokens.borders.card,
              borderRadius: '8px',
            }}
          />
          <Legend 
            wrapperStyle={{ color: 'rgb(107, 114, 128)' }}
            className="dark:[&_text]:fill-gray-400"
          />
          {series.map((s, index) => (
            <Bar
              key={s.id}
              dataKey={s.id}
              name={s.label}
              fill={colors[index % colors.length]}
              radius={[4, 4, 0, 0]}
              stackId="a" // Stack all bars
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
